import { GeospatialApi, Regions } from 'api/client';
import AccountBar from 'components/account-bar';
import Alert, { IconTypeEnum } from 'components/alert';
import Button from 'components/button';
import DownloadAppBanner from 'components/download-app-banner';
import { H4 } from 'components/headings';
import { HideVisually } from 'components/hide-visually';
import Layout from 'components/layout';
import PlanStatus from 'components/plan-status';
import {
  AccountPlanWrapper,
  AlertWrapper,
} from 'components/plan-status/plan-status.styles';
import { ButtonWrapper } from 'components/questions/question.styles';
import ReminderList from 'components/reminder-list';
import { ReminderHeadingWrapper } from 'components/reminder-list/reminder-list.styles';
import SEO from 'components/seo';
import ToolsCard from 'components/tools-card';
import ContentSection from 'components/ui/content-section';
import useAuth from 'hooks/use-auth';
import { usePlanAPI } from 'hooks/use-plan';
import { GetStaticProps, NextPage } from 'next';
import Link from 'next/link';
import { useEffectOnce } from 'react-use';
import AnalyticsService, { ScreenNames } from 'services/analytics';
import { backgroundImages } from 'utils/config';
import { getRegionByLocation } from 'utils/helpers';
import { protectRoute } from 'utils/protect-route';
import APP_ROUTES from 'utils/routes';

const PAGE_TITLE = 'My account';

interface Props {
  regions: Regions;
}

const Account: NextPage<Props> = ({ regions }) => {
  const { user } = useAuth();

  const planAPI = usePlanAPI();
  const { data: plans, isFetched } = planAPI.usePlans();

  useEffectOnce(() => {
    AnalyticsService.trackScreen(ScreenNames.dashboard);
  });

  return (
    <Layout
      mainBackground={backgroundImages.hero}
      afterMainContent={<DownloadAppBanner />}
    >
      <SEO title={PAGE_TITLE} />
      <HideVisually as='h1'>{PAGE_TITLE}</HideVisually>

      {!!user && (
        <ContentSection>
          <AccountPlanWrapper>
            <AccountBar name={user.displayName} />
            <H4 as='h2'>My bushfire plans</H4>
            {isFetched &&
              plans?.data.map((plan, index) => (
                <PlanStatus planData={plan} key={`plan-status-card-${index}`} />
              ))}
            {plans?.data.length === 0 && (
              <AlertWrapper>
                <Alert
                  iconType={IconTypeEnum.Alert}
                  iconPosition='center'
                  href={APP_ROUTES.home}
                  static
                >
                  You don't have a plan yet. <br />
                  <strong>Create your bushfire plan now.</strong>
                </Alert>
              </AlertWrapper>
            )}

            {/* Only show the create new plan button if the user already has plans,
            because if they don't we're already showing an alert module prompting the user to make a plan*/}
            {plans?.data.length !== 0 && (
              <ButtonWrapper>
                <Link href={APP_ROUTES.home} passHref>
                  <Button variant='default'>Create a new plan</Button>
                </Link>
              </ButtonWrapper>
            )}

            <ReminderHeadingWrapper>
              <H4 as='h2'>Reminders</H4>
            </ReminderHeadingWrapper>

            {isFetched && (
              <ReminderList
                region={getRegionByLocation(
                  plans?.data[0]?.plan.location,
                  regions
                )}
              />
            )}

            <ToolsCard
              icon='conversation'
              title='Do you have questions?'
              href='faq'
              description='Check the FAQs'
            />
            <ToolsCard
              icon='resource'
              title='Preparation checklists'
              href='emergency-resources'
              description='Download them here'
            />
          </AccountPlanWrapper>
        </ContentSection>
      )}
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const GeoApi = new GeospatialApi();
  const { data: regions } = await GeoApi.getRegions();

  return {
    props: {
      regions,
    },
  };
};

export default protectRoute(Account);
